import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload, Search, Pencil, Copy, Trash2, MoreVertical, CheckCircle2, FileEdit } from "lucide-react";
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { DropdownButton, AgTableCard } from "../UI";
import { getAgGridSortStorageKey } from '../UI/AgGrid';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import useComponentVisible from '../UI/ComponentVisible';
import { ExperienceContext } from '../../contexts/ExperienceContext';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { permission } from '../../config/permission';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import { createExperience, getFeatureOrgUsage, getUserProfile } from '../../utils/APIs';
import { getObjFromHtml, isValidJsonString } from '../../utils/helper';
import AddExperienceModal from './AddExperienceModal';
import DeleteExperienceWithDependencyModal from './DeleteExperienceWithDependencyModal';

const ManageExperiences = () => {
    const navigate = useNavigate();
    const { enums, content } = useContentfulContext();
    const [usageCount, setUsageCount] = useState(0);
    const [usageExceeded, setUsageExceeded] = useState(true);
    const [selectedAction, setSelectedAction] = useState('');
    const [selectedExperience, setSelectedExperience] = useState('');
    const { experiencesData, getAllExperiencesHandler, setExperiencesData, loading: contextLoading } = useContext(ExperienceContext);
    const [rowData, setRowData] = useState([]);
    const [showAddExperiencesModal, setShowAddExperiencesModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedExperiences, setSelectedExperiences] = useState([]);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const { ref: menuRef, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

    const { userData, setUserData, checkPermission } = useUserContext();
    const { user } = useAuth0();

    // Sort experiences by updatedAt descending
    const sortedExperiences = useMemo(() => {
        return [...experiencesData].sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
    }, [experiencesData]);

    // First published experience
    const firstPublishedExp = sortedExperiences.find(exp => exp.published_version);

    // First draft experience (no published version)
    const firstDraftExp = sortedExperiences.find(exp => !exp.published_version);

    const checkUsage = async () => {
        try {
            const result = await getFeatureOrgUsage(userData?.token, userData?.selectedOrganization?.org_token, 'experience.create');
            if (result) {
                const { current_usage, max_allowed_usage } = result.data;
                const exceed = max_allowed_usage !== -1 && current_usage >= max_allowed_usage;
                setUsageCount(max_allowed_usage - current_usage);
                if (exceed) {
                    const profileResult = await getUserProfile(userData.token, user.sub);
                    if (profileResult.data?.organizations.length > 0) {
                        setUserData((pre) => {
                            return { ...pre, ...profileResult.data };
                        });
                    }
                }
                setUsageExceeded(exceed);
            }
        } catch (err) {
            console.error('Error fetching usage data:', err);
        }
    };

    useEffect(() => {
        if (userData?.token && userData?.selectedOrganization?.org_token) {
            getAllExperiencesHandler();
            checkUsage();
        }
    }, [userData?.defaultAdobeIO?._id]);

    const extractUserName = (val) => {
        if (!val) return "";
        if (typeof val === 'string') return val;
        if (typeof val === 'object') return val.name || val.email || val.username || "";
        return String(val);
    };

    const formattedData = useMemo(() => {
        return experiencesData.map((item) => {
            let updatedExperienceJson;

            if (item?.templates.length > 0 && (item?.templates[0]?.type === enums?.templateTypes.json_a_to_html.id)) {
                const parsedJsonSchema = JSON.parse(item?.templates[0]?.json_schema);
                updatedExperienceJson = getObjFromHtml(item.experience_json, item?.templates[0], parsedJsonSchema.config_obj_key);
            } else {
                const isValidJson = isValidJsonString(item.experience_json);
                updatedExperienceJson = isValidJson ? JSON.parse(item.experience_json) : "";
            }
            const templateName = Array.isArray(item.templates) && item.templates.length > 0
                ? item.templates.map((x) => (typeof x === 'object' ? x?.name : x) || "Template").filter(Boolean).join('", "')
                : "Template deleted";
            const offerIds = [
                ...(item?.associated_adobe_target_offer_id || []),
                ...(item?.associated_contentful_offer_id || []),
                ...(item?.associated_tealium_offer_id || []),
                ...(item?.associated_github_entry_path || [])
            ];

            const authorName = extractUserName(item.user_id) || extractUserName(item.managed_by) || extractUserName(item.created_by) || "-";

            return {
                experience_name: typeof item.name === 'string' ? item.name : (item.name?.name || ""),
                published_version: item?.published_version,
                template_name: templateName,
                last_edited: formatDate(item.updatedAt),
                managed_by: authorName,
                offer_id: offerIds,
                id: item._id,
                experience_json: updatedExperienceJson,
                templates: item.templates
            };
        });
    }, [experiencesData, enums]);

    useEffect(() => {
        setRowData(formattedData);
    }, [formattedData]);

    const [gridApi, setGridApi] = useState(null);

    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("manage-experiences-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [
                    { colId: 'last_edited', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const isNameUnique = (name) => {
        return !experiencesData.some(item => item.name === name);
    };

    const duplicateName = (experienceName) => {
        let newName = `${experienceName} Copy`;
        let counter = 1;
        while (!isNameUnique(newName)) {
            newName = `${experienceName} Copy ${counter++}`;
        }
        return newName;
    };

    function formatDate(timestamp) {
        const date = new Date(timestamp);
        const optionsDate = {
            month: 'short',
            day: '2-digit',
            year: 'numeric'
        };
        const formattedDate = date.toLocaleDateString('en-US', optionsDate);
        const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
        let hours12 = date.getHours() % 12;
        hours12 = hours12 ? hours12 : 12;
        const formattedTime = hours12 + ':' + ('0' + date.getMinutes()).slice(-2) + ' ' + ampm;
        return formattedDate + ', ' + formattedTime;
    }

    const duplicateExperienceHandler = async () => {
        try {
            setLoading(true);
            const payload = {
                name: duplicateName(selectedExperience.experience_name),
                templates: [selectedExperience.templates[0]._id],
                html: '',
                css: '',
                base_config_json: '',
                experience_json: JSON.stringify(selectedExperience.experience_json),
                source: enums.experience_sources.stride.id
            };
            const result = await createExperience(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                payload,
                {
                    event_name: ANALYTICS_EVENTS.CREATE_EXPERIENCE,
                    metadata: { 
                        experience_name: payload.name, 
                        template_name: selectedExperience.templates[0].name, 
                        source: 'stride',
                        is_duplicate: true,
                        original_experience_name: selectedExperience.experience_name,
                        created: true
                    }
                }
            );
            const newExperiences = [...experiencesData];
            if (Array.isArray(result.data) && result.data.length) {
                newExperiences.push(result.data[0]);
                setExperiencesData(newExperiences);
                navigate(`/experience/edit-experience/${result.data[0]._id}`);
            }
        } catch (err) {
            setError(err?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    };

    const colDefs = [
        { headerName: "", field: "", checkboxSelection: true, headerCheckboxSelection: true, headerCheckboxSelectionFilteredOnly: true, width: 40 },
        {
            headerName: content.MANAGE_EXPERIENCE_CONTENT.table_headers.experience_name,
            cellRenderer: ({ data }) => {
                let buttonId = undefined;
                if (firstDraftExp?._id === data.id) {
                    buttonId = "edit-first-draft-experience-btn-id";
                } else if (firstPublishedExp?._id === data.id) {
                    buttonId = "edit-first-published-experience-btn-id";
                }
                const isPublished = data?.published_version;
                const nameStr = typeof data?.experience_name === 'object' ? data?.experience_name?.name : data?.experience_name;
                return (
                    <div className="flex items-center gap-2 min-w-0 h-full">
                        {isPublished ? (
                            <span title="Approved" aria-label="Approved" className="inline-flex items-center justify-center text-emerald-600 shrink-0">
                                <CheckCircle2 size={16} />
                            </span>
                        ) : (
                            <span title="Draft" aria-label="Draft" className="inline-flex items-center justify-center text-amber-500 shrink-0">
                                <FileEdit size={15} />
                            </span>
                        )}
                        <button
                            id={buttonId}
                            type="button"
                            className="min-w-0 truncate text-stride-heading hover:text-stride-primary font-semibold text-left transition-colors cursor-pointer bg-transparent border-0 p-0 text-[13px]"
                            title={nameStr}
                            onClick={() => {
                                setSelectedExperience(data);
                                navigate(`/experience/experience-details/${data.id}`);
                            }}
                        >
                            {nameStr}
                        </button>
                    </div>
                );
            },
            field: "experience_name", filter: true, suppressMovable: true
        },
        { 
            headerName: content.MANAGE_EXPERIENCE_CONTENT.table_headers.template_name, 
            field: "template_name",
            cellRenderer: ({ data }) => {
                const name = data?.template_name;
                if (!name || name === "Template deleted") {
                    return <span className="text-stride-muted text-xs italic">Template deleted</span>;
                }
                return (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-surface-alt text-stride-muted border border-border-subtle" title={name}>
                        {name}
                    </span>
                );
            },
            filter: true, 
            suppressMovable: true 
        },
        {
            headerName: content.MANAGE_EXPERIENCE_CONTENT.table_headers.last_edited,
            field: "last_edited",
            cellRenderer: ({ data }) => (
                <span className="text-xs text-stride-muted tabular-nums font-medium">
                    {data?.last_edited || "-"}
                </span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            suppressMovable: true,
            sortable: true
        },
        {
            headerName: content.MANAGE_EXPERIENCE_CONTENT.table_headers.managed_by,
            field: "managed_by",
            cellRenderer: ({ data }) => {
                const val = data?.managed_by;
                const name = typeof val === 'object' ? val?.name || val?.email : val;
                return (
                    <span className="text-xs font-medium text-stride-body">
                        {name || "-"}
                    </span>
                );
            },
            sortable: false,
            resizable: true,
            suppressMovable: true
        },
        {
            headerName: content.MANAGE_EXPERIENCE_CONTENT.table_headers.offer_id,
            field: "offer_id",
            cellRenderer: ({ data }) => {
                const list = Array.isArray(data?.offer_id) ? data.offer_id : [];
                const displayList = list
                    .map((item) => (typeof item === 'object' ? item?.name || item?.id || '' : String(item || '')))
                    .filter(Boolean);
                const text = displayList.join(", ");
                if (!text) return <span className="text-stride-muted text-xs">—</span>;
                return (
                    <span className="text-xs text-stride-body truncate" title={text}>
                        {text}
                    </span>
                );
            },
            sortable: false,
            resizable: true,
            suppressMovable: true
        },
        {
            headerName: 'Actions',
            field: 'actions',
            width: 150,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: ({ data }) => {
                if (!data) return null;
                return (
                    <div className="stride-table-actions">
                        <button
                            type="button"
                            className="stride-table-btn stride-table-btn-edit"
                            title="Edit experience"
                            onClick={() => {
                                setSelectedAction('edit');
                                navigate(`/experience/edit-experience/${data.id}`);
                            }}
                        >
                            <Pencil size={14} />
                        </button>
                        <button
                            type="button"
                            className="stride-table-btn stride-table-btn-copy"
                            title="Duplicate experience"
                            onClick={() => {
                                setSelectedExperience(data);
                                setSelectedAction('duplicate');
                                duplicateExperienceHandler();
                            }}
                        >
                            <Copy size={14} />
                        </button>
                        <button
                            type="button"
                            className="stride-table-btn stride-table-btn-delete"
                            title="Delete experience"
                            onClick={() => {
                                setSelectedExperience(data);
                                setSelectedAction('delete');
                            }}
                        >
                            <Trash2 size={14} />
                        </button>
                        <button
                            type="button"
                            className="stride-table-btn actions"
                            title="More options"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedExperience(data);
                                setIsComponentVisible(!isComponentVisible);
                                const buttonRect = e.currentTarget.getBoundingClientRect();
                                setMenuPosition({
                                    top: buttonRect.bottom + window.scrollY,
                                    left: buttonRect.left + window.scrollX
                                });
                            }}
                        >
                            <MoreVertical size={14} />
                        </button>
                    </div>
                );
            },
        },
    ];

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 40,
            columnLimits: [
                {
                    colId: "experience_name",
                    minWidth: 260,
                },
                {
                    colId: "template_name",
                    minWidth: 200,
                },
                {
                    colId: "last_edited",
                    minWidth: 160,
                },
                {
                    colId: "managed_by",
                    minWidth: 140,
                },
                {
                    colId: "offer_id",
                    minWidth: 140,
                },
                {
                    colId: "actions",
                    minWidth: 150,
                }
            ],
        };
    }, []);

    const handleCloseModal = () => {
        setShowAddExperiencesModal(false);
    };

    const handleRowSelected = (event) => {
        const selectedRows = event.api.getSelectedRows();
        const selectedExpData = experiencesData.filter(exp => selectedRows.map(row => row.id).includes(exp._id));
        setSelectedExperiences(selectedExpData);
    };

    const onFilterTextBoxChanged = (event) => {
        const filterValue = (event?.target?.value || '').toLowerCase();
        if (gridApi) {
            gridApi.setFilterModel(filterValue ? {
                experience_name: { type: 'contains', filter: filterValue }
            } : null);
        }
    };

    const navigateToBulkPreview = () => {
        if (enums.MAX_SELECTED_RECORDS_FOR_BULK_ACTION < selectedExperiences.length) {
            toast.error(`Maximum ${enums.MAX_SELECTED_RECORDS_FOR_BULK_ACTION} experiences can be selected for bulk preview.`);
            return;
        }
        navigate('/experience/bulk-preview', {
            state: {
                experiences: selectedExperiences
            }
        });
    };

    const navigateToActivation = () => {
        if (enums.MAX_SELECTED_RECORDS_FOR_BULK_ACTION < selectedExperiences.length) {
            toast.error(`Maximum ${enums.MAX_SELECTED_RECORDS_FOR_BULK_ACTION} experiences can be selected for bulk activation.`);
            return;
        }
        navigate('/experience/bulk-activation', {
            state: {
                experiences: selectedExperiences
            }
        });
    };

    return (
        <>
            {error && <ErrorAlert timeout={content.MANAGE_EXPERIENCE_CONTENT.timeout}>{error}</ErrorAlert>}
            <main id="main" className="stride-page-shell bg-gradient-to-b from-surface via-white to-white min-h-screen space-y-6">
                {/* Header & Actions */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <p className="stride-eyebrow">Experience Management</p>
                        <div className="flex items-baseline gap-3">
                            <h1 className="text-2xl md:text-3xl stride-heading m-0">
                                {content.MANAGE_EXPERIENCE_CONTENT.title}
                            </h1>
                            <span className="text-xs md:text-sm text-stride-muted font-normal">
                                {rowData.length} {rowData.length === 1 ? 'experience' : 'experiences'}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
                        {selectedExperiences && selectedExperiences?.length > 1 && (
                            <DropdownButton
                                title={content.MANAGE_EXPERIENCE_CONTENT.cta_text.bulk_action}
                                buttons={[
                                    checkPermission(permission.MANAGE_EXP_OP_BULK_ACTION_PREVIEW) && { title: content.MANAGE_EXPERIENCE_CONTENT.cta_text.bulk_preview, onClick: navigateToBulkPreview },
                                    checkPermission(permission.MANAGE_EXP_OP_BULK_ACTION_ACTIVATE) && { title: content.MANAGE_EXPERIENCE_CONTENT.cta_text.bulk_activation, onClick: navigateToActivation },
                                    { title: content.MANAGE_EXPERIENCE_CONTENT.cta_text.bulk_delete, onClick: () => setSelectedAction('delete') },
                                ].filter(Boolean)}
                            />
                        )}
                        {!usageExceeded && (
                            <>
                                {checkPermission(permission.MANAGE_EXP_OP_BULK_ACTION_UPLOAD) && (
                                    <Button
                                        id="bulk-upload-experience-btn-id"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowAddExperiencesModal(true)}
                                        icon={<Upload size={14} />}
                                    >
                                        {content.MANAGE_EXPERIENCE_CONTENT.cta_text.bulk_upload}
                                    </Button>
                                )}
                                <Button
                                    id="create-experience-btn-id"
                                    variant="primary"
                                    size="sm"
                                    onClick={() => navigate('/experience/create-experience')}
                                    icon={<Plus size={15} />}
                                >
                                    {content.MANAGE_EXPERIENCE_CONTENT.cta_text.create_experience}
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Filter and Table Container */}
                <AgTableCard
                    header={
                        <>
                            <div className="w-full sm:w-80">
                                <Input
                                    prefixIcon={<Search size={15} />}
                                    placeholder={content.MANAGE_EXPERIENCE_CONTENT.search_placeholder}
                                    onChange={onFilterTextBoxChanged}
                                    clearable
                                />
                            </div>
                            {selectedExperiences.length > 0 && (
                                <div className="flex items-center gap-2 text-xs font-semibold text-primary bg-primary-soft px-3 py-1.5 rounded-lg border border-primary/20">
                                    <span>{selectedExperiences.length} selected</span>
                                </div>
                            )}
                        </>
                    }
                    tableId="manage-experiences-table"
                    theme="ag-theme-quartz"
                    loading={loading || contextLoading}
                    rowData={rowData}
                    columnDefs={colDefs}
                    autoSizeStrategy={autoSizeStrategy}
                    onGridReady={onGridReady}
                    rowSelection="multiple"
                    suppressRowClickSelection
                    onRowSelected={handleRowSelected}
                    pagination={true}
                    paginationPageSize={content.MANAGE_EXPERIENCE_CONTENT.default_page_size || 10}
                    paginationPageSizeSelector={enums.PAGINATION_PAGE_SIZE_SELECTOR}
                >
                    {isComponentVisible && selectedExperience && (
                        <div
                            ref={menuRef}
                            className="absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1"
                            style={{ top: menuPosition.top, left: Math.max(10, menuPosition.left - 180) }}
                        >
                            <div className="py-1">
                                <a
                                    href="#"
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                    tabIndex="-1"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('edit');
                                        navigate(`/experience/edit-experience/${selectedExperience.id}`);
                                    }}
                                >
                                    {content.MANAGE_EXPERIENCE_CONTENT.more_info_options.edit}
                                </a>
                                <a
                                    href="#"
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                    tabIndex="-1"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('duplicate');
                                        duplicateExperienceHandler();
                                    }}
                                >
                                    {content.MANAGE_EXPERIENCE_CONTENT.more_info_options.duplicate}
                                </a>
                                <a
                                    href="#"
                                    className="text-rose-600 block px-4 py-2 text-sm hover:bg-gray-100"
                                    tabIndex="-1"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('delete');
                                    }}
                                >
                                    {content.MANAGE_EXPERIENCE_CONTENT.more_info_options.delete}
                                </a>
                            </div>
                        </div>
                    )}
                </AgTableCard>

                <AddExperienceModal
                    showAddExperiencesModal={showAddExperiencesModal}
                    handleCloseModal={handleCloseModal}
                    fetchOrgUsage={checkUsage}
                    usageCount={usageCount}
                />

                {selectedAction === 'delete' && (
                    <DeleteExperienceWithDependencyModal
                        selectedExperience={selectedExperiences.length > 0 ? selectedExperiences : selectedExperience}
                        setSelectedAction={setSelectedAction}
                        setSelectedExperience={setSelectedExperience}
                        setSelectedExperiences={setSelectedExperiences}
                    />
                )}
            </main>
        </>
    );
};

export default ManageExperiences;
